import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface FilterSelectionMarkProps {
  selected: boolean;
  /**
   * `round` for a list that takes one choice, `square` for one that takes
   * several: the shape says which, so it must match how the list behaves.
   */
  shape: "round" | "square";
  className?: string;
}

/**
 * The indicator at the front of a filter row: an empty ring when the row is off,
 * a filled mark with a check when it is on. It sits next to the label, so the
 * cue is not a tick at the far edge of the row. Decorative: the row itself
 * reports whether it is pressed. In dark mode the filled mark is mint; in light
 * it is the foreground colour. See [[filter-selection-mark.spec]].
 */
export function FilterSelectionMark({
  selected,
  shape,
  className,
}: FilterSelectionMarkProps) {
  return (
    <span
      aria-hidden="true"
      data-slot="filter-selection-mark"
      data-selected={selected}
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center border-[1.5px] transition-colors",
        shape === "round" ? "rounded-full" : "rounded-[5px]",
        selected
          ? "border-foreground bg-foreground text-background dark:border-primary dark:bg-primary dark:text-primary-foreground"
          : "border-muted-foreground/40",
        className,
      )}
    >
      {selected && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  );
}
